import { where, type QueryConstraint } from '@/lib/firestore'
import { useAuthStore } from '@/stores/auth'

/**
 * Returns a Firestore `where` constraint to filter by tenantId,
 * or null if the current user is a superadmin without impersonation (sees all data).
 *
 * Respects impersonation: if superadmin is impersonating a tenant,
 * filters by the impersonated tenantId.
 *
 * Usage in stores:
 *   const tenantConstraint = getTenantConstraint()
 *   const constraints = [orderBy('createdAt', 'desc')]
 *   if (tenantConstraint) constraints.unshift(tenantConstraint)
 *   const q = query(collection(db, 'projects'), ...constraints)
 */
export function getTenantConstraint(): QueryConstraint | null {
  const authStore = useAuthStore()
  const tenantId = authStore.effectiveTenantId
  if (tenantId) return where('tenantId', '==', tenantId)

  // Sin tenant efectivo:
  // - superadmin → sin filtro (ve todo, permitido por las rules).
  // - cualquier otro (incl. ventana transitoria donde el user aún no cargó) → NUNCA correr
  //   una query cross-tenant: devolvemos un constraint imposible → resultado vacío en vez de
  //   'permission-denied'. Cuando effectiveTenantId resuelva, la query real corre filtrada.
  if (authStore.isSuperAdmin) return null
  return where('tenantId', '==', '__no_tenant__')
}

/**
 * Constraint por MEMBRESÍA para la colección `users`: filtra por `tenantIds array-contains
 * <tenant activo>`, no por el `tenantId` primario. Así un usuario aparece en TODOS los tenants
 * a los que pertenece (no solo su primario). Mismo hardening que getTenantConstraint.
 */
export function getMembershipConstraint(): QueryConstraint | null {
  const authStore = useAuthStore()
  const tenantId = authStore.effectiveTenantId
  if (tenantId) return where('tenantIds', 'array-contains', tenantId)
  if (authStore.isSuperAdmin) return null
  return where('tenantIds', 'array-contains', '__no_tenant__')
}

/**
 * Returns the tenantId to stamp on new documents, or undefined if not applicable.
 * Respects impersonation.
 */
export function getTenantIdForCreate(): string | undefined {
  const authStore = useAuthStore()
  return authStore.effectiveTenantId || undefined
}

/**
 * Returns an object with tenantId field if applicable, for spreading into Firestore data.
 * Respects impersonation.
 * Usage: const data = { ...getTenantData(), name: 'foo', ... }
 */
export function getTenantData(): { tenantId?: string } {
  const tenantId = getTenantIdForCreate()
  if (!tenantId) return {}
  return { tenantId }
}
